import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'antd';
import { Modal } from '@app/components/common/Modal/Modal';
import * as S from './ExamMockTestDetail.styles';
import { notificationController } from '@app/controllers/notificationController';
import {
  IMockTestUserRequest,
  IMockTestUserUpdate,
  mockTestFinish,
  mockTestResume,
  mockTestUpdate,
  saveProgressMockTest,
} from '@app/api/mockTest.api';
import { RenderQuestion } from '../../renderQuestion';
import { IExam } from '@app/api/academic.api';
import { ExamMockTestFinish } from '../ExamMockTestFinish';
import { ExamMockTestBreak } from '../ExamMockTestBreak';
import { useLocation, useNavigate } from 'react-router-dom';
import { convertTypeParctice } from '@app/utils/utils';
import ProgressBar from '../component/ProgressBar';
import CountdownTimer from '../component/CountdownTimer';
import { Select, Option } from '@app/components/common/selects/Select/Select';
import { RootState } from '@app/store/store';
import { useDispatch, useSelector } from 'react-redux';
import { setAllowNext, setIsCompleteAudio } from '@app/store/slices/mockTestSlice';
import { useTranslation } from 'react-i18next';
import { MOCK_TEST_TYPE } from '@app/constants/mockTest';
import {
  FULL_TEST_STEP_DURATIONS,
  FULL_TEST_STEPS,
  SECTION_TEST_STEP_DURATIONS,
  SECTION_TEST_STEPS,
} from './mockTestSteps';

let STEP_DURATIONS: Record<string, number> = {};
let STEPS: string[] = [];

export const ExamMockTestDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(location.search);
  const mockTestId = queryParams.get('id');
  const mockTestType = queryParams.get('type');
  const isFirstRender = useRef(false);
  const { allowNext } = useSelector((state: RootState) => state.mockTest);

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [questions, setQuestions] = useState<IExam[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [step, setStep] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [currentAnswer, setCurrentAnswer] = useState<string | Blob | null>(null);
  const [progress, setProgress] = useState(0);
  const [mockTestName, setMockTestName] = useState('');
  const [modalNotSkip, setModalNotSkip] = useState(false);
  const [modalNext, setModalNext] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);
  const [mockTestUserId, setMockTestUserId] = useState('');

  const handleGetTime = (time: number) => {
    setCurrentTime(time);
  };

  useEffect(() => {
    initTestByType(mockTestType || '');
    fetchQuestions();
  }, [mockTestId]);

  const initTestByType = (type: string) => {
    if (type === MOCK_TEST_TYPE.FULL_TEST) {
      STEP_DURATIONS = FULL_TEST_STEP_DURATIONS;
      STEPS = FULL_TEST_STEPS;
      setStep(FULL_TEST_STEPS[0]);
    } else if ((type === MOCK_TEST_TYPE.SECTION_TEST || type === MOCK_TEST_TYPE.QUESTION_TEST) && type) {
      const durations = SECTION_TEST_STEP_DURATIONS[type];

      const steps = SECTION_TEST_STEPS[type];

      if (durations && steps) {
        STEP_DURATIONS = durations;
        STEPS = steps;
        setStep(steps[0]);
      }
    }
  };

  const currentQuestions = useMemo(() => {
    if (mockTestType === MOCK_TEST_TYPE.FULL_TEST) {
      return questions.filter((q) => q.type.startsWith(step));
    } else {
      return questions;
    }
  }, [questions, step]);

  const currentIndexView = useMemo(() => {
    return questions.findIndex((item) => item.id === currentQuestions[currentIndex]?.id);
  }, [questions, currentQuestions, currentIndex]);

  const fetchQuestions = async () => {
    if (!mockTestId) return;
    setLoading(true);
    try {
      const res = await mockTestResume(mockTestId);
      const { questions, userMockTest } = res;
      setQuestions(questions);
      setMockTestName(userMockTest.mocktestname);
      setMockTestUserId(userMockTest.id);

      const questionType =
        mockTestType === MOCK_TEST_TYPE.FULL_TEST
          ? res.questions.filter((q) => q.type.startsWith(res.userMockTest.step))
          : res.questions;

      const isLastQuestion = questionType.length === userMockTest.currentquestion;

      //next step when last question
      if (isLastQuestion) {
        const nextStepIndex = STEPS.indexOf(res.userMockTest.step) + 1;
        setCurrentIndex(0);
        setStep(STEPS[nextStepIndex]);
        setTimeLeft(STEP_DURATIONS[STEPS[nextStepIndex]]);
      } else {
        setCurrentIndex(res.userMockTest.currentquestion);
        setStep(res.userMockTest.step);
        setTimeLeft(res.userMockTest.remaintime);
      }
    } catch (error) {
      notificationController.error({ message: `Xảy ra lỗi. Vui lòng thử lại sau` });
    } finally {
      setLoading(false);
    }
  };

  // const currentQuestions = questions.filter((q) => q.type.startsWith(step));
  // const currentIndexView = questions.findIndex((item) => item.id === currentQuestions[currentIndex]?.id);

  useEffect(() => {
    if (currentQuestions.length > 0) {
      const progressValue = ((currentIndex + 1) / currentQuestions.length) * 100;
      setProgress(progressValue);
    } else {
      setProgress(0); // Nếu không có câu hỏi, reset về 0
    }
  }, [currentIndexView, questions]);

  const handleTimeout = () => {
    setTimeout(() => {
      setIsModalVisible(false);
      setCurrentAnswer(null);
      const nextStepIndex = STEPS.indexOf(step) + 1;

      if (nextStepIndex < STEPS.length) {
        setStep(STEPS[nextStepIndex]);
        setCurrentIndex(0);
        setTimeLeft(STEP_DURATIONS[STEPS[nextStepIndex]]);
      } else {
        setStep('FINISH');
        onFinished();
      }
    }, 1000);
  };

  const resolveRef = useRef<((value: string | Blob) => void) | null>(null);
  const waitForCurrentAnswer = () => {
    return new Promise<string | Blob>((resolve) => {
      resolveRef.current = resolve;
    });
  };

  const handleGotoNext = async () => {
    if (!mockTestId) return;
    try {
      let answer = currentAnswer;

      if (isSpeaking && !answer) {
        dispatch(setIsCompleteAudio(true));
        const updatedAnswer = await waitForCurrentAnswer();
        answer = updatedAnswer;
      }
      setModalNotSkip(false);
      setIsModalVisible(false);
      setModalNext(false);
      dispatch(setAllowNext(false));
      dispatch(setIsCompleteAudio(false));

      const formData = new FormData();
      formData.append('mockTestId', mockTestId);
      formData.append('questionId', currentQuestions[currentIndex]?.id);
      formData.append('questionType', currentQuestions[currentIndex]?.type);
      formData.append('currentQuestion', (currentIndex + 1).toString());
      formData.append('remainTime', currentTime?.toString() || '');
      formData.append('step', step);

      if (answer instanceof Blob) {
        formData.append('file', answer);
        formData.append('answer', '');
      } else {
        formData.append('answer', answer || '');
      }

      //save current progress
      await saveProgressMockTest(formData);

      setCurrentAnswer(null);
      // Logical handling of question transfer
      if (currentIndex < currentQuestions.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        const nextStep = STEPS[STEPS.indexOf(step) + 1];

        if (nextStep) {
          setStep(nextStep);
          setCurrentIndex(0);
          setTimeLeft(STEP_DURATIONS[nextStep]);
        } else {
          handleTimeout();
        }
      }
    } catch (error) {
      notificationController.error({
        message: 'Xảy ra lỗi. Vui lòng thử lại sau',
      });
    }
  };

  const handleSkip = () => {
    const nextStep = STEPS[STEPS.indexOf(step) + 1];
    if (nextStep) {
      setStep(nextStep);
      setCurrentIndex(0);
      setTimeLeft(STEP_DURATIONS[nextStep]);
    }
  };

  const handleSaveExit = () => {
    navigate(`/exam/mock-test?id=${mockTestId}`);
  };

  const onFinished = async () => {
    try {
      if (!mockTestId) return;

      const payload: IMockTestUserRequest = { mockTestId };
      await mockTestFinish(payload);
    } catch (error) {
      notificationController.error({
        message: 'Xảy ra lỗi. Vui lòng thử lại sau',
      });
    }
  };

  const handleAnswerChange = (answer: string | Blob) => {
    setCurrentAnswer(answer);
    if (isSpeaking) setModalNext(true);
    if (resolveRef.current) {
      resolveRef.current(answer);
      resolveRef.current = null;
    }
  };

  const handleConfirm = () => {
    if (isSpeaking && !allowNext) {
      setModalNotSkip(true);
      return;
    }
    setIsModalVisible(true);
  };

  const handleQuestionChange = (value: unknown) => {
    setSelectedQuestion(value as string);
  };

  const handleGotoQuestion = async () => {
    try {
      if (!selectedQuestion) return;

      const splitString = selectedQuestion.split('_');
      const payload: IMockTestUserUpdate = { step: splitString[0], current_question: Number(splitString[1]) };
      const res = await mockTestUpdate(mockTestUserId, payload);

      if (res.statusCode === 200) {
        fetchQuestions();
        dispatch(setAllowNext(false));
      }
    } catch (error) {
      notificationController.error({
        message: 'Xảy ra lỗi. Vui lòng thử lại sau',
      });
    }
  };

  const isSpeaking = currentQuestions[currentIndex]?.type.startsWith('SPEAKING');

  return (
    <>
      {step == 'FINISH' ? (
        <ExamMockTestFinish />
      ) : (
        <>
          {currentQuestions.length > 0 && (
            <>
              <S.TopBar>
                <div className="text-base">{mockTestName}</div>
                <div className="flex gap-2">
                  <p className="p-2 text-sm ">
                    {t('page.results.question')} {currentIndexView + 1} of {questions.length}
                  </p>
                  <CountdownTimer initialTime={timeLeft} onTimeout={handleTimeout} onGetTime={handleGetTime} />
                </div>
              </S.TopBar>
              <S.Header>
                <p className="text-base">{convertTypeParctice(currentQuestions[currentIndex]?.type)}</p>
              </S.Header>
              <ProgressBar progress={progress} />
              <S.Wrapper>
                <S.FadeInRight key={currentIndex}>
                  <div className="text-base font-semibold mb-2">
                    {/* {convertQuestionByType(currentQuestions[currentIndex]?.type)} */}
                    {t(`pteSection.${currentQuestions[currentIndex]?.type}`)}
                  </div>
                  <RenderQuestion
                    type={currentQuestions[currentIndex]?.type}
                    currentQuestion={currentQuestions[currentIndex]}
                    showAnswer={false}
                    isFirstRender={isFirstRender}
                    onAnswerChange={handleAnswerChange}
                    canStart={false}
                    allowDrag={false}
                    allowClipboardActions={false}
                  />
                </S.FadeInRight>
              </S.Wrapper>

              <S.BtnFooter>
                <Button type="primary" onClick={handleSaveExit}>
                  {t('page.mockTest.button.saveExit')}
                </Button>
                <div className="flex items-center gap-3">
                  <Select width={150} onChange={handleQuestionChange}>
                    {(() => {
                      const typeIndexMap = new Map<string, number>();
                      let i = 0;

                      return questions.map((question) => {
                        const typePrefix = question.type.split('_')[0];
                        const index = typeIndexMap.get(typePrefix) || 0;
                        typeIndexMap.set(typePrefix, index + 1);
                        i++;
                        return (
                          <Option key={question.id} value={`${typePrefix}_${index}`}>
                            {i}
                          </Option>
                        );
                      });
                    })()}
                  </Select>
                  <Button type="primary" onClick={handleGotoQuestion}>
                    Go
                  </Button>
                  <Button type="primary" onClick={handleConfirm}>
                    {t('page.mockTest.button.next')}
                  </Button>
                </div>
              </S.BtnFooter>
            </>
          )}

          {(step == 'BREAK1' || step == 'BREAK2') && <ExamMockTestBreak onSkip={handleSkip} />}
        </>
      )}

      <Modal
        title={t('page.mockTest.modal.title_confirm')}
        visible={isModalVisible}
        onOk={handleGotoNext}
        onCancel={() => setIsModalVisible(false)}
        cancelText={t('common.cancel')}
      >
        <p>{t('page.mockTest.modal.content_confirm')}</p>
      </Modal>

      <Modal
        title={t('page.mockTest.modal.title_skip')}
        visible={modalNotSkip}
        footer={[]}
        closable={false}
        maskClosable={true}
        onCancel={() => setModalNotSkip(false)}
      >
        {t('page.mockTest.modal.content_skip')}
      </Modal>

      <Modal
        title={t('page.mockTest.modal.title_recording_stopped')}
        visible={modalNext}
        footer={[
          <Button key="next" type="primary" onClick={handleGotoNext}>
            {t('page.mockTest.button.next')}
          </Button>,
        ]}
        closable={false}
        maskClosable={true}
      >
        {t('page.mockTest.modal.content_recording_stopped')}
      </Modal>
    </>
  );
};
